import { Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { generatePath, useHistory } from "react-router-dom";

import { Button } from "../../components/Button";
import { ShiftQualificationPill } from "../../Shift/QualificationPill";
import { SUBMIT_UNVERIFIED_TIMESHEET_FULL_PATH } from "../path";

interface ShiftHistoryCardFooterProps {
  shiftId: string;
  totalPayInDollars: number;
  qualificationText: string;
  isVerified: boolean;
  isMissedPunchRequestEnabled: boolean;
  hasPendingExtraWorkedTimeRequest: boolean;
  isTimesheetUploaded: boolean;
  onUploadTimesheetClick: () => void;
  onChatWithSupportClick: () => void;
}

export function ShiftHistoryCardFooter(props: ShiftHistoryCardFooterProps) {
  const {
    shiftId,
    totalPayInDollars,
    qualificationText: qualificationName,
    isVerified,
    isMissedPunchRequestEnabled,
    hasPendingExtraWorkedTimeRequest,
    isTimesheetUploaded,
    onUploadTimesheetClick,
    onChatWithSupportClick,
  } = props;

  const history = useHistory();

  const showEarnedPay = totalPayInDollars > 0 && isVerified;

  const handleRequestShiftAdjustment = () => {
    const path = generatePath(SUBMIT_UNVERIFIED_TIMESHEET_FULL_PATH, { shiftId });
    history.push(path);
  };

  function getButtonConfig(): {
    label: string;
    variant: "outlined" | "contained";
    onClick?: () => void;
  } {
    if (hasPendingExtraWorkedTimeRequest) {
      return {
        label: "Payment request in review",
        variant: "outlined",
      };
    }

    if (isMissedPunchRequestEnabled) {
      return {
        label: isVerified ? "Request pay adjustment" : "Request shift payment",
        variant: isVerified ? "outlined" : "contained",
        onClick: handleRequestShiftAdjustment,
      };
    }

    if (isVerified) {
      return {
        label: "Request pay adjustment",
        variant: "outlined",
        onClick: onChatWithSupportClick,
      };
    }

    return {
      label: "Request shift payment",
      variant: isTimesheetUploaded ? "outlined" : "contained",
      onClick: onUploadTimesheetClick,
    };
  }

  const buttonConfig = getButtonConfig();

  return (
    <Stack direction="column" spacing={2}>
      <Stack direction="row" justifyContent="space-between">
        <ShiftQualificationPill qualificationText={qualificationName} />

        <Text semibold variant="h5">
          {showEarnedPay && `${formatDollarsAsUsd(totalPayInDollars)} earned`}
        </Text>
      </Stack>
      <Box sx={{ pt: 4 }}>
        <Stack direction="column" alignItems="center">
          <Button
            fullWidth
            variant={buttonConfig.variant}
            size="medium"
            disabled={hasPendingExtraWorkedTimeRequest}
            onClick={buttonConfig.onClick}
          >
            {buttonConfig.label}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
